import { useState } from 'react'
import { Badge, Textarea } from '@/components/ui'
import { AlertTriangle, Lock, Check, X, CheckCircle2, XCircle } from 'lucide-react'
import { formatDateTime } from '@/lib/timeFormatters'
import type { WorkflowRunApprovalRequest } from '@/types'
import { RejectPhaseModal } from './RejectPhaseModal'

interface Props {
  approvalRequest: WorkflowRunApprovalRequest
  canApprove: boolean
  isRoleAuthorized: boolean
  onApprove: (phaseId: string, comment?: string) => void
  onReject: (phaseId: string, reason: string) => void
  isApproving?: boolean
  isRejecting?: boolean
  approveDisabled?: boolean
  approveDisabledReason?: string
}

export function ApprovalBanner({ approvalRequest, canApprove, isRoleAuthorized, onApprove, onReject, isApproving, isRejecting, approveDisabled, approveDisabledReason }: Props) {
  const [comment, setComment] = useState('')
  const [rejectModalOpen, setRejectModalOpen] = useState(false)

  if (approvalRequest.status === 'Approved') {
    return (
      <div className="rounded-lg border border-success-foreground bg-success p-4" data-testid="approval-banner" role="alert">
        <div className="flex items-center gap-2 text-success-foreground">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Gate Approved</p>
            <p className="text-sm">
              Approved by {approvalRequest.decidedBy} on {formatDateTime(approvalRequest.decidedAt)}
              {approvalRequest.comment && <span> &mdash; &ldquo;{approvalRequest.comment}&rdquo;</span>}
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (approvalRequest.status === 'Rejected') {
    return (
      <div className="rounded-lg border border-danger-foreground bg-danger p-4" data-testid="approval-banner" role="alert">
        <div className="flex items-center gap-2 text-danger-foreground">
          <XCircle className="h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">Gate Rejected</p>
            <p className="text-sm">
              Rejected by {approvalRequest.decidedBy} on {formatDateTime(approvalRequest.decidedAt)}
              {approvalRequest.comment && <span> &mdash; &ldquo;{approvalRequest.comment}&rdquo;</span>}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="rounded-lg border border-warning-foreground bg-warning p-4" data-testid="approval-banner" role="alert">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-semibold text-warning-foreground">
              <AlertTriangle className="h-4 w-4" />
              Approval
            </div>
            <Badge variant="outline" className="text-xs gap-1 bg-input-background text-warning-foreground border-warning-foreground">
              <Lock className="h-3 w-3" /> Gate
            </Badge>
          </div>

          <p className="text-sm text-warning-foreground">
            {approvalRequest.message || 'Approval Required: Workflow paused. Review previous phase results and approve or reject.'}
          </p>

          {approvalRequest.approvalRoles && approvalRequest.approvalRoles.length > 0 && (
            <p className="text-xs text-warning-foreground">
              Restricted to: {approvalRequest.approvalRoles.join(', ')}
            </p>
          )}

          {canApprove && (
            <>
              <div>
                <p className="text-sm font-medium text-warning-foreground mb-1">Comment</p>
                <Textarea
                  placeholder="Multiline text"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  className="bg-input-background"
                  disabled={!isRoleAuthorized}
                />
              </div>
              <div className="flex justify-end gap-4">
                <button
                  className="flex items-center gap-1 text-sm font-medium text-danger-foreground hover:text-danger-foreground disabled:opacity-50"
                  onClick={() => setRejectModalOpen(true)}
                  disabled={isRejecting || !isRoleAuthorized}
                  aria-label="Reject phase"
                >
                  <X className="h-4 w-4" /> Reject
                </button>
                <button
                  className="flex items-center gap-1 text-sm font-medium text-success-foreground hover:text-success-foreground disabled:opacity-50"
                  onClick={() => onApprove(approvalRequest.phaseId, comment.trim() || undefined)}
                  disabled={isApproving || !isRoleAuthorized || approveDisabled}
                  title={approveDisabledReason}
                  aria-label="Approve phase"
                >
                  <Check className="h-4 w-4" /> Approve
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <RejectPhaseModal
        phaseName={approvalRequest.phaseName}
        open={rejectModalOpen}
        onOpenChange={setRejectModalOpen}
        initialReason={comment}
        onConfirm={(reason) => {
          onReject(approvalRequest.phaseId, reason)
          setRejectModalOpen(false)
        }}
        isPending={isRejecting}
      />
    </>
  )
}
